import { withErrorBoundary } from "app/ErrorBoundary/ErrorBoundaryDashboard";
import RenderNoDataMessage, {
  RenderMessage,
} from "features/VehicleSidebar/VehicleList/common/RenderNoDataMessage";
import { ResultList } from "features/VehicleSidebar/VehicleList/common/ResultList";
import { ResultListWrapper } from "features/VehicleSidebar/VehicleList/common/ResultListWrapper";
import { useFilter } from "features/VehicleSidebar/VehicleList/Filtering/useFilter";
import { filterReserveAlternativeTransport } from "features/VehicleSidebar/VehicleList/Filtering/vehicleFiltering";
import { VehicleSearchFilter } from "features/VehicleSidebar/VehicleList/Filtering/VehicleSearchFilter";
import { ReserveOrBackupAlternativeTransportElement } from "features/VehicleSidebar/VehicleList/ReserveAlternativeTransportList/ReserveOrBackupAlternativeTransportElement";
import { BackupAlternativeTransportElement } from "features/VehicleSidebar/VehicleList/ReserveAlternativeTransportList/BackupAlternativeTransportElement";
import { FC, useMemo } from "react";
import { generatePath, useLocation } from "react-router-dom";
import { reserveAlternativeTransportListResponseSchema } from "shared/types/alternativeTransport";
import { sortReserveTransport } from "shared/utils/alternativeTransport";
import { BACKUPBUS, RESERVEBUS } from "shared/utils/routes";
import z from "zod";
import { processTransportList } from "features/VehicleSidebar/VehicleList/AddVehicles/utils";

type ReserveAlternativeTransportListProps = {
  searchQuery: string;
  showOldVehicles: boolean;
  isReserveVehicles: boolean;
  vehiclesList: z.infer<
    typeof reserveAlternativeTransportListResponseSchema
  >["vehicles"];
};

const ReserveOrBackupAlternativeTransportList: FC<
  ReserveAlternativeTransportListProps
> = ({ searchQuery, showOldVehicles, isReserveVehicles, vehiclesList }) => {
  const { pathname } = useLocation();
  const filterOptions = ["Skiftnr", "Stasjon", "Telefonnr"];

  const {
    selectedFilter,
    handleFilterChange,
    filterOptions: options,
  } = useFilter(filterOptions);

  const processedList = useMemo(
    () =>
      processTransportList({
        transportList: vehiclesList,
        showOldVehicles,
        timeKey: "shiftEndTime",
      }),
    [vehiclesList, showOldVehicles],
  );

  const filteredVehiclesList = useMemo(
    () =>
      vehiclesList.filter((vehicle) =>
        filterReserveAlternativeTransport(vehicle, searchQuery, selectedFilter),
      ),
    [vehiclesList, searchQuery, selectedFilter],
  );

  const listToShow =
    searchQuery.length > 0 ? filteredVehiclesList : processedList;

  const pathName = (altransId: string) => {
    const PATH = isReserveVehicles ? RESERVEBUS : BACKUPBUS;
    return generatePath(PATH, { vehicleUuid: altransId });
  };
  return (
    <>
      <VehicleSearchFilter
        searchQuery={searchQuery}
        resultLength={listToShow.length}
        filterOptions={options}
        selectedFilter={selectedFilter}
        handleFilterChange={handleFilterChange}
      />

      <ResultListWrapper>
        <ResultList
          id={`AlternativeTransportList-${isReserveVehicles ? "Reserve" : "Backup"}`}
        >
          {listToShow.length > 0 ? (
            listToShow
              .sort(sortReserveTransport)
              .map((alternativeTransport, index) =>
                isReserveVehicles ? (
                  // Rewriting one component at a time so this is a temporary duplicate of the ReserveOrBackupAlternativeTransportElement component
                  <ReserveOrBackupAlternativeTransportElement
                    key={alternativeTransport.id}
                    reserveAlternativeTransport={alternativeTransport}
                    elementSelected={
                      pathname === pathName(alternativeTransport.id)
                    }
                    isReserveVehicle={isReserveVehicles}
                    index={index + 1}
                  />
                ) : (
                  <BackupAlternativeTransportElement
                    key={alternativeTransport.id}
                    reserveAlternativeTransport={alternativeTransport}
                    elementSelected={
                      pathname === pathName(alternativeTransport.id)
                    }
                    index={index + 1}
                  />
                ),
              )
          ) : (
            <RenderNoDataMessage
              processedList={processedList}
              renderMessage={RenderMessage.Reserve}
            />
          )}
        </ResultList>
      </ResultListWrapper>
    </>
  );
};

export default withErrorBoundary(ReserveOrBackupAlternativeTransportList);
